import api from '@api';
import AgentReference from '@components/common/AgentReference';
import config from '@config';
import { WindowSize } from '@enums';
import { getToken } from '@platform2-lib/token';
import { getClassName, getStorageItem, log } from '@utils';
import { EventSource } from 'eventsource';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import {
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useRemark } from 'react-remark';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import type { ChatResponse, PromptMessage } from 'types/common';
import type { ApiError, Audience } from 'types/schema';

const token = getToken(config.env);

/**
 * A hook that fetches a chat with Server-Sent Events (SSE) and handles the response.
 *
 * @param prompt - The prompt to send to the server
 * @param chatId - The ID of the chat
 * @returns An object containing the server error, event source, incoming message,
 *          and reply completion status
 */
export function useEventSourceChat(data: PromptMessage, chatId: string) {
  const [serverError, setServerError] = useState<ApiError | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [incomingMessage, setIncomingMessage] = useState<ChatResponse | null>(
    null
  );
  const [isReplyCompleted, setIsReplyCompleted] = useState(false);
  const isProperReplyRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    // Skip if the prompt is empty
    if (!data.prompt) {
      return;
    }

    const headers = {
      ['Accept']: 'text/event-stream',
      ['Content-Type']: 'application/json',
      ['Authorization']: `Bearer ${token}`
    };
    const body = JSON.stringify({
      ...data,
      // Include the chat ID to continue the conversation
      ['chat_id']: chatId,
      // Remove the timestamp from the prompt
      ['prompt']: data.prompt.split('__')[0],
      ['agent']: config.chat.sseAgent
    });

    log('Sending new request to the server...', {
      data: { headers, body }
    });

    const newEventSource = new EventSource(
      `${config.api.origin}/spark-agent-v2/chat/sse`,
      {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            method: 'POST',
            headers,
            body
          })
      }
    );

    setEventSource(newEventSource);

    // Move this to cleanup function
    if (isReplyCompleted) {
      setIsReplyCompleted(false);
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: OK
    const handleEvent = (event: MessageEvent) => {
      try {
        const message: ChatResponse = JSON.parse(event.data);

        if (event.type === 'chat_init') {
          log(message['message'], { background: 'limegreen' });
          log(`Trace Id: ${message.metadata['trace_id']}`, {
            background: 'steelblue'
          });
          return;
        }

        if (event.type === 'end') {
          const isProperReply = isProperReplyRef.current;

          if (!isProperReply) {
            const message =
              'The server failed to send a "natural language" message.';
            console.warn(message);
            setServerError({ message, type: 'natural_language' });
          }

          setIsReplyCompleted(true);

          // Reset the proper reply flag
          isProperReplyRef.current = false;

          // Close the connection before the server does it
          newEventSource.close();

          return;
        }

        if (event.type === 'natural_language') {
          if (getStorageItem('octopus.chat.ignore_natural_language_messages')) {
            return;
          }

          isProperReplyRef.current = true;
        }

        // Set the incoming message and thinking status
        setIncomingMessage(message);
      } catch (error) {
        console.error('Failed to process server-sent event message:', error);
      }
    };

    // Listen for all the specific event types your server sends
    newEventSource.addEventListener('chat_init', handleEvent);
    newEventSource.addEventListener('loading', handleEvent);
    newEventSource.addEventListener('natural_language', handleEvent);
    newEventSource.addEventListener('end', handleEvent);
    // Also listen for any untagged messages (fallback)
    newEventSource.addEventListener('message', handleEvent);

    newEventSource.onerror = (event) => {
      const message = event.message || 'Unknown error';
      console.error(`Server-Sent Events: ${message}`, event);
      setServerError({ message });
      setIsReplyCompleted(true);
      newEventSource.close();
    };

    return () => {
      // Remove event listeners
      newEventSource.removeEventListener('chat_init', handleEvent);
      newEventSource.removeEventListener('loading', handleEvent);
      newEventSource.removeEventListener('natural_language', handleEvent);
      newEventSource.removeEventListener('end', handleEvent);
      newEventSource.removeEventListener('message', handleEvent);

      // Close the connection
      newEventSource.close();
    };
  }, [data.prompt]);

  return {
    cancelReply: () => eventSource?.close(),
    incomingMessage,
    isReplyCompleted,
    serverError
  };
}

/**
 * Returns detailed data for each audience in the input array by fetching from the API.
 * Preserves the original `is_outdated` flag. If input is empty or a single blank ID,
 * returns the input directly. Returns `null` while loading.
 *
 * @param audiences Array of audiences to look up
 * @returns Enriched audiences, or `null` while loading
 */
export function useAudienceDetails(audiences?: Audience[]): Audience[] | null {
  const [details, setDetails] = useState<Audience[] | null>(null);

  useEffect(() => {
    if (!audiences || audiences.length === 0) {
      return;
    }

    if (audiences.length === 1 && audiences[0].id === '') {
      setDetails(audiences);
      return;
    }

    const audienceIds = audiences.map(({ id }) => id);

    api.audiences
      .getAudienceDetails(audienceIds)
      .then((data) => {
        setDetails(
          data.map((item, index) => ({
            ...item,
            ['is_outdated']: audiences[index]?.['is_outdated']
          }))
        );
      })
      .catch(() => setDetails([...audiences]));
  }, [audiences]);

  return details;
}

/**
 * Hook to get the current window size category and helpers, updating on resize.
 *
 * Uses predefined breakpoints to categorize window width into WindowSize enums.
 * Provides helper booleans for common layout logic (isLarge, isMedium, isSmall).
 * Returns `null` for value until window is measured on mount.
 *
 * @returns isLarge, isMedium, isSmall, value
 *
 * @example
 *   const { isSmall, value } = useWindowSize();
 *   if (isSmall) renderMobile(); else renderDesktop();
 */
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<WindowSize | null>(null);
  const isDispatched = useRef(false);

  const handleWindowResize = useCallback(() => {
    let currentSize: WindowSize;

    if (window.innerWidth <= config.breakpoints.xSmall) {
      currentSize = WindowSize.ExtraExtraSmall;
    } else if (window.innerWidth < config.breakpoints.small) {
      currentSize = WindowSize.ExtraSmall;
    } else if (window.innerWidth < config.breakpoints.medium) {
      currentSize = WindowSize.Small;
    } else if (window.innerWidth < config.breakpoints.large) {
      currentSize = WindowSize.Medium;
    } else if (window.innerWidth < config.breakpoints.xLarge) {
      currentSize = WindowSize.Large;
    } else if (window.innerWidth < config.breakpoints.xxLarge) {
      currentSize = WindowSize.ExtraLarge;
    } else {
      currentSize = WindowSize.ExtraExtraLarge;
    }

    // Update the window size only if it changes
    if (currentSize !== windowSize) {
      setWindowSize(currentSize);

      if (windowSize !== null) {
        log(`Screen size changed to ${currentSize}.`);
      }
    }
  }, [windowSize]);

  useEffect(() => {
    if (!isDispatched.current) {
      handleWindowResize();
      isDispatched.current = true;
    }

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  return {
    isExtraLarge:
      windowSize === WindowSize.ExtraLarge ||
      windowSize === WindowSize.ExtraExtraLarge,
    isLarge:
      WindowSize.Large === windowSize ||
      WindowSize.ExtraLarge === windowSize ||
      WindowSize.ExtraExtraLarge === windowSize,
    isMedium: windowSize === WindowSize.Medium,
    isSmall:
      windowSize === WindowSize.Small ||
      windowSize === WindowSize.ExtraSmall ||
      windowSize === WindowSize.ExtraExtraSmall,
    isExtraSmall:
      windowSize === WindowSize.ExtraSmall ||
      windowSize === WindowSize.ExtraExtraSmall,
    value: windowSize
  };
}

/**
 * Hook to monitor an element's size and determine if it matches certain
 * breakpoint conditions.
 *
 * @param element - DOM element observed for size changes.
 * @param testBreakpointRanges - Function taking element width (inlineSize)
 *   and returning a boolean array, with each value indicating if a specific
 *   breakpoint is satisfied.
 * @returns Boolean indicating if the element's width matches any breakpoint.
 *
 * @example
 * ```tsx
 * function ResponsiveComponent() {
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   // Check if container width matches breakpoints
 *   const isWithinBreakpoint = useElementBreakpoints(
 *     containerRef.current,
 *     (width) => [width < 768, width >= 768 && width < 1024]
 *   );
 *
 *   return (
 *     <div ref={containerRef}>
 *       {isWithinBreakpoint ? 'Breakpoint matched' : 'Not matched'}
 *     </div>
 *   );
 * }
 * ```
 */
export function useElementBreakpoints(
  element: Element,
  testBreakpointRanges?: (inlineSize: number) => boolean[]
) {
  const [withinBreakpointRange, setWithinBreakpointRange] = useState(false);
  const timeoutRef = useRef<number>(-1);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (entry.borderBoxSize) {
        const [borderBoxSize] = entry.borderBoxSize;
        const ok = testBreakpointRanges(borderBoxSize.inlineSize).some(Boolean);

        if (ok !== withinBreakpointRange) {
          // Cancel the previous timeout
          window.clearTimeout(timeoutRef.current);
          // Add a small delay to ensure a smooth transition
          timeoutRef.current = window.setTimeout(
            () => setWithinBreakpointRange(ok),
            200
          );
        }
      }
    });

    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [element, testBreakpointRanges, withinBreakpointRange]);

  return withinBreakpointRange;
}

/**
 * Custom hook that wraps `useRemark` for enhanced Markdown rendering.
 *
 * Injects custom React components for Markdown elements, enabling features
 * like interactive tooltips, agent links, and source references in rendered
 * Markdown.
 *
 * @remarks
 * - Extends Markdown with app-specific UI components.
 * - Handles special link protocols (e.g., `spark://`) for in-app navigation.
 *
 * @returns Result of `useRemark` with custom components.
 *
 * @example
 * ```tsx
 * const markdown = useMarkdown();
 * return (
 *   <div>
 *     {markdown.render(
 *       'See the [sources](spark://sources?total=3&insights=abc,def,ghi).'
 *     )}
 *   </div>
 * );
 * ```
 */
function useMarkdown(enableWordFade = false) {
  const rehypeReactOptions = {
    components: {
      a: AgentReference,
      blockquote: ({ children }: { children: ReactNode }) => (
        <blockquote>{children}</blockquote>
      )
    },
    createElement: safeCreateElement
  };

  const sanitizeSchema = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      span: [...(defaultSchema.attributes?.span ?? []), 'className']
    },
    protocols: {
      ...(defaultSchema.protocols ?? {}),
      href: ['http', 'https', 'mailto', 'spark']
    }
  } as const;

  return useRemark({
    rehypeReactOptions,
    remarkPlugins: [remarkGfm, remarkBreaks],
    rehypePlugins: enableWordFade
      ? [rehypeLastWordFade, [rehypeSanitize, sanitizeSchema]]
      : [[rehypeSanitize, sanitizeSchema]]
  });
}

/** Rehype plugin — wraps the trailing word in `<span class="word-fade">`. */
function rehypeLastWordFade() {
  // biome-ignore lint/suspicious/noExplicitAny: rehype AST node
  return (tree: any) => {
    fadeLastWord(tree);
  };
}

// biome-ignore lint/suspicious/noExplicitAny: rehype AST traversal
function fadeLastWord(node: any): boolean {
  for (let i = node.children.length - 1; i >= 0; i--) {
    const child = node.children[i];

    if (child.type === 'element') {
      if (fadeLastWord(child)) {
        return true;
      }
      continue;
    }

    if (child.type !== 'text') {
      continue;
    }

    const match = child.value.match(/^([\s\S]*?)(\S+)(\s*)$/);

    if (!match) {
      continue;
    }

    const [, prefix, lastWord, suffix] = match;
    const replacement = [];

    if (prefix) {
      replacement.push({ type: 'text', value: prefix });
    }

    replacement.push({
      type: 'element',
      tagName: 'span',
      properties: { className: ['word-fade'] },
      children: [{ type: 'text', value: lastWord }]
    });

    if (suffix) {
      replacement.push({ type: 'text', value: suffix });
    }

    node.children.splice(i, 1, ...replacement);
    return true;
  }

  return false;
}

const tableRegExp = new RegExp(/^\|.+\|$/);
const whitespaceTokenRegExp = /(\s+)/;
const listItemRegExp = /^(\s*)([*+-]|\d+[.)])\s+(.*)$/;

function splitWithWhitespace(text: string) {
  return text.split(whitespaceTokenRegExp).filter((token) => token !== '');
}

function tokenizeLine(line: string) {
  const listMatch = listItemRegExp.exec(line);

  if (listMatch) {
    const [, indent, marker, rest] = listMatch;
    const restTokens = splitWithWhitespace(rest);

    if (restTokens.length === 0) {
      return [`${indent}${marker}`];
    }

    const [firstToken, ...remainingTokens] = restTokens;
    return [`${indent}${marker} ${firstToken}`, ...remainingTokens];
  }

  return splitWithWhitespace(line);
}

function* emitTextTokens(content: string) {
  const lines = content.split('\n');

  for (let index = 0; index < lines.length; index++) {
    const lineTokens = tokenizeLine(lines[index]);

    for (const token of lineTokens) {
      if (!token) {
        continue;
      }

      yield token;
    }

    if (index < lines.length - 1) {
      yield '\n';
    }
  }
}

function hasStringStyle(
  props: Parameters<typeof createElement>[1]
): props is Parameters<typeof createElement>[1] & { style: string } {
  return (
    !!props &&
    typeof props === 'object' &&
    'style' in props &&
    typeof (props as { style?: unknown }).style === 'string'
  );
}

function toReactStyleProperty(property: string) {
  const trimmed = property.trim();

  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('--')) {
    return trimmed;
  }

  return trimmed.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
}

function parseStyleAttribute(styleText: string): CSSProperties {
  const styleObject: Record<string, string> = {};

  for (const declaration of styleText.split(';')) {
    const [property, ...valueParts] = declaration.split(':');

    if (!property || valueParts.length === 0) {
      continue;
    }

    const value = valueParts
      .join(':')
      .replace(/!important/gi, '')
      .trim();
    const propertyName = toReactStyleProperty(property);

    if (!propertyName) {
      continue;
    }

    if (!value) {
      continue;
    }

    styleObject[propertyName] = value;
  }

  return styleObject;
}

function normalizeStyleProps(
  props: Parameters<typeof createElement>[1]
): Parameters<typeof createElement>[1] {
  if (!hasStringStyle(props)) {
    return props;
  }

  const normalizedProps = {
    ...(props as unknown as Record<string, unknown>),
    style: parseStyleAttribute(props.style)
  };

  return normalizedProps as Parameters<typeof createElement>[1];
}

const safeCreateElement = ((
  type: Parameters<typeof createElement>[0],
  props: Parameters<typeof createElement>[1],
  ...children: Parameters<typeof createElement>[2][]
) =>
  createElement(
    type,
    normalizeStyleProps(props),
    ...children
  )) as typeof createElement;

/**
 * A hook that streams words from a text at a configurable interval.
 *
 * This hook splits the provided text into words and streams them
 * one by one at the specified delay interval, building up the complete text
 * over time. If delay is set to 0 or skip is true, the content is rendered
 * immediately without streaming.
 *
 * Tables are always rendered immediately to preserve markdown structure.
 *
 * @param text - The input text to be streamed word by word
 * @param delay - The time interval in milliseconds between adding words
 *                (default: 50ms)
 * @param skip - Whether to skip streaming and render immediately
 * @param tokensPerTick - How many tokens to emit per interval tick (default: 1)
 * @returns A tuple with the rendered content and a completion flag
 *
 * @example
 * ```tsx
 * function TextStreamer() {
 *   const text = "This is an example of word streaming.";
 *   const [streamedContent, isComplete] = useWordStream(text);
 *   // Or with custom delay:
 *   // const [streamedContent, isComplete] = useWordStream(text, 100);
 *
 *   return <div data-finished={isComplete}>{streamedContent}</div>;
 * }
 * ```
 */
export function useWordStream(
  text: string,
  delay = 50,
  skip = false,
  tokensPerTick = 1
): [wordStream: ReactNode, isComplete: boolean] {
  const [stream, setStream] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [streamContent, setStreamSource] = useMarkdown(true);
  const [staticContent, setStaticSource] = useMarkdown(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Split content into table and non-table sections
  const parseContent = useCallback((content: string) => {
    const sections: Array<{ type: 'table' | 'text'; content: string }> = [];
    const lines = content.split('\n');
    let buffer: string[] = [];
    let currentType: 'table' | 'text' | null = null;

    for (const line of lines) {
      const lineType = tableRegExp.test(line.trim()) ? 'table' : 'text';

      // Flush buffer and start new section
      if (currentType !== null && currentType !== lineType) {
        sections.push({ type: currentType, content: buffer.join('\n') });
        buffer = [];
      }

      buffer.push(line);
      currentType = lineType;
    }

    // Flush remaining buffer
    if (buffer.length > 0 && currentType !== null) {
      sections.push({ type: currentType, content: buffer.join('\n') });
    }

    return sections;
  }, []);

  const splitTextIntoWords = useCallback(
    function* () {
      const sections = parseContent(text);

      for (const section of sections) {
        if (section.type === 'table') {
          // Surround tables with blank lines so Markdown treats them as a block
          yield '\n\n' + section.content + '\n\n';
          continue;
        }

        // Stream line by line so list markers ship with their first word,
        // avoiding transient heading mis-parsing while streaming
        yield* emitTextTokens(section.content);
      }
    },
    [text, parseContent]
  );

  // Static content stays in sync with the full text (no word-fade spans)
  useEffect(() => {
    setStaticSource(text);
  }, [text, setStaticSource]);

  useEffect(() => {
    // Reset stream on text change
    setStream([]);

    if (delay === 0) {
      return;
    }

    const generator = splitTextIntoWords();

    const tokensThisTick = Math.max(1, tokensPerTick);

    const intervalId = window.setInterval(() => {
      const batch: string[] = [];
      let streamEnded = false;

      for (let i = 0; i < tokensThisTick; i++) {
        const { value, done } = generator.next();

        if (done) {
          streamEnded = true;
          break;
        }

        batch.push(value as string);
      }

      if (batch.length > 0) {
        setStream((prev) => [...prev, ...batch]);
      }

      if (streamEnded) {
        clearInterval(intervalId);
        setIsComplete(true);
      }
    }, delay);

    return () => clearInterval(intervalId);
  }, [delay, tokensPerTick, splitTextIntoWords]);

  useEffect(() => {
    if (delay > 0 && stream.length > 0) {
      // Preserve original whitespace so nested lists render correctly
      setStreamSource(stream.join(''));
    }
  }, [stream, setStreamSource, delay]);

  // Retrigger the fade-in on the trailing word each render, since React
  // reuses the same <span> across ticks and wouldn't otherwise replay it.
  useLayoutEffect(() => {
    const last = containerRef.current?.querySelector<HTMLElement>('.word-fade');

    if (!last) {
      return;
    }

    last.style.animation = 'none';
    // Force a synchronous reflow so the browser commits `animation: none`
    // before the next assignment — otherwise both writes collapse into one
    // style recalc and the animation keeps running instead of restarting.
    void last.offsetWidth;
    last.style.animation = '';
  });

  const isStreaming = delay > 0 && !isComplete;

  if (skip) {
    return [staticContent, isComplete];
  }

  const wordStream = (
    <div
      ref={containerRef}
      className={getClassName({
        // 'with-cursor': !isComplete,
        'word-stream-content': true
      })}
      data-finished={isComplete}>
      {isStreaming ? streamContent : staticContent}
    </div>
  );

  return [wordStream, isComplete];
}

/**
 * Custom hook that returns a debounced version of the input value.
 *
 * It waits for a specified delay after the latest input value change before
 * updating the returned value.
 *
 * @param value - The input value to be debounced
 * @param delay - The delay in msecs before updating the value (default: 350)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebouncedValue(searchTerm);
 *
 * // debouncedSearchTerm will only update 350ms after searchTerm stops changing
 * ```
 */
export function useDebouncedValue(value: string, delay: number = 350) {
  const [debouncedValue, setDebouncedValue] = useState(value.trim());
  const timeoutRef = useRef<number>(-1);

  useEffect(() => {
    // Capture input after the delay is over
    timeoutRef.current = window.setTimeout(() => {
      setDebouncedValue(value.trim());
    }, delay);

    // Cleanup function to clear the timeout on unmount or input change
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook to detect if the current device is a touch device.
 *
 * Uses multiple detection methods for reliable touch device identification:
 * - Checks for 'ontouchstart' in window
 * - Checks navigator.maxTouchPoints
 * - Uses CSS media query '(pointer: coarse)'
 *
 * @returns boolean indicating if the device supports touch input
 *
 * @example
 * ```tsx
 * const isTouchDevice = useTouchDevice();
 * if (isTouchDevice) {
 *   // Show mobile-friendly UI
 * }
 * ```
 */
export function useTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * Calls `onClickOutside` when a pointer event occurs outside the given element.
 *
 * @param ref - Element treated as the inside boundary
 * @param enabled - Whether the document listener is active
 * @param onClickOutside - Callback fired for outside pointer events
 *
 * @example
 * ```tsx
 * const panelRef = useRef<HTMLDivElement>(null);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * useClickOutside(panelRef, isOpen, () => setIsOpen(false));
 * ```
 */
export function useClickOutside(
  ref: RefObject<Element | null>,
  enabled: boolean,
  onClickOutside: () => void
) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (ref.current?.contains(target)) {
        return;
      }

      onClickOutside();
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [enabled, onClickOutside, ref]);
}
export { useInsightsQuery } from './useInsightsQuery';
